import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import {
  getAllEndpointsStatus,
  getEndpointStatus,
  toggleEndpoint,
  enableEndpoint,
  disableEndpoint,
  getDefaultHeaders,
  getApiConfiguration,
} from "../../api/apiManagement";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

export default function ApiManagement() {
  const [endpoints, setEndpoints] = useState({});
  const [headers, setHeaders] = useState({});
  const [config, setConfig] = useState({});
  const [selectedKey, setSelectedKey] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filteredData, setFilteredData] = useState(Object.entries(endpoints));
  console.log(endpoints);

  // filter data
  // const filteredData = endpoints.filter((item) =>
  //   item.componentKey.toLowerCase().includes(search.toLowerCase())
  // );
  const keys = useMemo(() => {
    return Object.keys(endpoints || {})
      .sort()
      .filter((k) => k.toLowerCase().includes(search.toLowerCase()));
  }, [endpoints, search]);
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [eps, hdrs, cfg] = await Promise.all([
        getAllEndpointsStatus(),
        getDefaultHeaders(),
        getApiConfiguration(),
      ]);
      setEndpoints(eps || {});
      setHeaders(hdrs || {});
      setConfig(cfg || {});
      if (selectedKey) {
        try {
          const st = await getEndpointStatus(selectedKey);
          setSelected(st || null);
        } catch {}
      }
    } catch (e) {
      setError("Failed to load API management data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (fn, okMsg) => {
    if (!selectedKey) return;
    setLoading(true);
    setError("");
    try {
      await fn(selectedKey);
      setSuccess(okMsg);
      await load();
    } catch (e) {
      setError("Operation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Admin: API Management
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search component..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Card sx={{ maxHeight: "620px", overflow: "auto" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1">Endpoints</Typography>
                <IconButton onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Box>
              <List dense>
                {keys.map((k) => (
                  <ListItem
                    key={k}
                    button
                    onClick={async () => {
                      setSelectedKey(k);
                      try {
                        const st = await getEndpointStatus(k);
                        setSelected(st || null);
                      } catch {}
                    }}
                  >
                    <ListItemText
                      primary={k}
                      secondary={endpoints[k]?.pathPattern || ""}
                    />
                    <Chip
                      label={endpoints[k]?.enabled ? "Enabled" : "Disabled"}
                      color={endpoints[k]?.enabled ? "success" : "default"}
                      size="small"
                    />
                  </ListItem>
                ))}
                {keys.length === 0 && (
                  <Typography variant="body2">No endpoints</Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Selected Endpoint
              </Typography>
              {!selectedKey && (
                <Typography variant="body2">Select an endpoint</Typography>
              )}
              {selectedKey && (
                <>
                  <Typography variant="body2">Key: {selectedKey}</Typography>
                  <Typography variant="body2">
                    Pattern: {selected?.pathPattern}
                  </Typography>
                  <Typography variant="body2">
                    Enabled: {String(selected?.enabled)}
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      disabled={loading}
                      onClick={() => act(toggleEndpoint, "Toggled")}
                    >
                      Toggle
                    </Button>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => act(enableEndpoint, "Enabled")}
                    >
                      Enable
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={loading}
                      onClick={() => act(disableEndpoint, "Disabled")}
                    >
                      Disable
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Default Headers
              </Typography>
              {Object.keys(headers || {}).length === 0 && (
                <Typography variant="body2">None</Typography>
              )}
              {Object.entries(headers || {}).map(([k, v]) => (
                <Box
                  key={k}
                  sx={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Typography variant="body2">{k}</Typography>
                  <Typography variant="body2">{String(v)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card> */}

          {/* <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Configuration
              </Typography>
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {JSON.stringify(config, null, 2)}
              </pre>
            </CardContent>
          </Card> */}
        </Grid>
      </Grid>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        message={error}
      />
      <Snackbar
        open={!!success}
        autoHideDuration={2500}
        onClose={() => setSuccess("")}
        message={success}
      />
    </Box>
  );
}
